// BankVerification.jsx
import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { CheckCircle, AlertCircle, Loader2, CreditCard, Shield, Zap } from 'lucide-react';
import { subscriptionService } from '../../_services';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY)

const BankVerification = ({ onVerificationComplete, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('initial');
  const [accountDetails, setAccountDetails] = useState(null);
  const [error, setError] = useState(null);
  const [stripe, setStripe] = useState(null);

  useEffect(() => {
    const initStripe = async () => {
      const stripeInstance = await stripePromise;
      setStripe(stripeInstance);
    };
    initStripe();
  }, []);

  const handleInstantVerification = async () => {
    if (!stripe) {
      setError('Stripe not initialized');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const sessionData = await subscriptionService.createFinancialConnectionsSession();
      const res = await stripe.collectBankAccountToken({clientSecret: sessionData.data.client_secret});
      if (res.error) {
        throw new Error(res.error.message);
      }

      if(!res?.financialConnectionsSession?.accounts || res.financialConnectionsSession.accounts.length == 0) {
        throw new Error("No account added");
      }

      const verificationResult = await subscriptionService.processVerificationResult(res.financialConnectionsSession.accounts);
      if (verificationResult.status) {
        setAccountDetails(verificationResult.data[0].accountDetails);
        setVerificationStatus('verified');
        if (onVerificationComplete) {
          onVerificationComplete(verificationResult.data[0].accountDetails);
        }
      } else {
        throw new Error(verificationResult.error);
      }

    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message || 'Verification failed. Please try again.');
      setVerificationStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const renderInitialState = () => (
    <div className="tw-text-center tw-space-y-6">
      <div className="tw-mx-auto tw-w-16 tw-h-16 tw-bg-blue-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
        <CreditCard className="tw-w-8 tw-h-8 tw-text-blue-600" />
      </div>
      
      <div>
        <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Verify Your Bank Account</h2>
        <p className="tw-text-gray-600">
          To add your bank account as a payment method, it must be verified.
        </p>
      </div>


      <div className="tw-space-y-3 tw-flex tw-flex-col">
        <button
          onClick={handleInstantVerification}
          disabled={isLoading}
          className="btn btn-primary"
        >
          {isLoading ? (
            <Loader2 className="tw-w-5 tw-h-5 tw-animate-spin" />
          ) : (
            <>
              <span>Connect Bank Account Instantly</span>
            </>
          )}
        </button>
        <button
        onClick={() => {
          if(onClose) {
            onClose()
          } else {
            setVerificationStatus('initial');
            setAccountDetails(null);
          }
        }}
        className="btn btn-secondary"
        >
          Close
        </button>
        
      </div>

      <p className="tw-text-xs tw-text-gray-500">
        By continuing, you agree to our Terms of Service and Privacy Policy.
        Your bank login credentials are never stored by our service.
      </p>
    </div>
  );

  const renderVerifiedState = () => (
    <div className="tw-text-center tw-space-y-6">
      <div className="tw-mx-auto tw-w-16 tw-h-16 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
        <CheckCircle className="tw-w-8 tw-h-8 tw-text-green-600" />
      </div>
      
      <div>
        <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Account Verified!</h2>
        <p className="tw-text-gray-600">
          Your bank account has been successfully verified and is ready to use.
        </p>
      </div>

      <div className="tw-bg-gray-50 tw-rounded-lg tw-p-6 tw-text-left">
        <h3 className="tw-font-semibold tw-text-gray-900 tw-mb-4">Account Details</h3>
        <div className="tw-space-y-2 tw-text-sm">
          <div className="tw-flex tw-justify-between">
            <span className="tw-text-gray-600">Bank:</span>
            <span className="tw-font-medium">{accountDetails?.bankName}</span>
          </div>
          <div className="tw-flex tw-justify-between">
            <span className="tw-text-gray-600">Account Type:</span>
            <span className="tw-font-medium">{accountDetails?.accountType}</span>
          </div>
          <div className="tw-flex tw-justify-between">
            <span className="tw-text-gray-600">Account:</span>
            <span className="tw-font-medium">****{accountDetails?.lastFour}</span>
          </div>
          <div className="tw-flex tw-justify-between">
            <span className="tw-text-gray-600">Account Holder:</span>
            <span className="tw-font-medium">{accountDetails?.accountHolderName}</span>
          </div>
        </div>
      </div>
      <div className="tw-space-y-3 tw-flex tw-flex-col">
      <button
        onClick={() => {
          setVerificationStatus('initial');
          setAccountDetails(null);
        }}
        className="btn btn-primary"
      >
        Continue
      </button>
      <button
        onClick={() => {
          if(onClose) {
            onClose()
          } else {
            setVerificationStatus('initial');
            setAccountDetails(null);
          }
        }}
        className="btn btn-secondary"
      >
        Close
      </button>
      </div>
    </div>
  );


  const renderErrorState = () => (
    <div className="tw-text-center tw-space-y-6">
      <div className="tw-mx-auto tw-w-16 tw-h-16 tw-bg-red-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
        <AlertCircle className="tw-w-8 tw-h-8 tw-text-red-600" />
      </div>
      
      <div>
        <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Verification Failed</h2>
        <p className="tw-text-gray-600">
          We couldn't verify your account at this time. Please try again
        </p>
      </div>

      {error && (
        <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4">
          <p className="tw-text-red-700 tw-text-sm">{error}</p>
        </div>
      )}

      <div className="tw-space-y-3 tw-flex tw-flex-col">
        <button
          onClick={handleInstantVerification}
          className="btn btn-primary"
        >
          Try Again
        </button>
        <button
        onClick={() => {
          if(onClose) {
            onClose()
          } else {
            setVerificationStatus('initial');
            setAccountDetails(null);
          }
        }}
        className="btn btn-secondary"
      >
        Close
      </button>
      </div>
      
    </div>
  );

  return (
    <div className="tw-bg-white tw-rounded-xl tw-shadow-lg tw-max-w-md tw-w-full tw-p-8">
      {verificationStatus === 'initial' && renderInitialState()}
      {verificationStatus === 'verified' && renderVerifiedState()}
      {verificationStatus === 'error' && renderErrorState()}
    </div>
  );
};

export default BankVerification;